import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Head from './components/header/head.js';
import LandingPage from './components/landing-page/landing.js';
import Overlay from './components/overlay-wrap/overlay-wrap.js';
import ScoringCore from './components/scoring/core/scoring-core.js';
import MediaPlayer from './components/media-player/media-player.js';
import LoadingBlock from './components/loading-block/loading-block.js';
import { beforeHotLoad, clear_hot_load } from './javascript/utils/game-utils.js';
import PWAUpdateHandler from './components/pwa-update-handler.js';
import { ServiceLauncher, ServiceLauncherHotLoad } from './javascript/services/services.js';
import { Sleep, Wait_For_Auth, isHotLoad, ConfirmModel, Logout } from './javascript/utils/utils.js';
import useIsLandscape from './javascript/utils/use-is-landscape.js';
import { OVERLAY, GLOBAL_VARS, load_DB_vars, NAV_CONTROL, SME_AUTH } from './javascript/services/globalVars.js';
import DBHandler from './javascript/index-db/db-handler.js';
import './style/App.css';
import './style/appShare.css';

// Survives React Strict Mode remounts — prevents duplicate init race conditions
let appInitPromise = null;

function App() {
  const isLandscape = useIsLandscape();
  const [loading, setLoading] = useState(() => true);
  const [free_app_status, setFreeAppStatus] = useState(() => false);
  const [overlayVisibility, setOverlayVisibility] = useState(0);
  const [overlayElement, setOverlayElement] = useState((<></>));
  const hideOverlay = () => { setOverlayVisibility(0); }
  const showOverlay = () => { setOverlayVisibility(1); }
  const setOverlayContent = (element) => { setOverlayElement(element); }

  OVERLAY.hide = hideOverlay;
  OVERLAY.show = showOverlay;
  OVERLAY.element = setOverlayContent;
  OVERLAY.sts = overlayVisibility;
  const commonTriggers = { overlay: OVERLAY }
  const mediaHost = import.meta.env.VITE_MEDIA_HOST;

  const SubscribeBtn = () => {
    window.open(mediaHost + 'pricing.php#ScoringApp', '_blank');
  }
  const LogoutBtn = () => {
    Logout();
  }


  useEffect(() => {
    // console.log('GLOBAL_VARS.free_app: ', GLOBAL_VARS.free_app);
    const checkAndSetFreeAppStatus = () => {
      setFreeAppStatus(GLOBAL_VARS.free_app);
    };

    // Initial check on mount
    checkAndSetFreeAppStatus();

    // Poll for changes to GLOBAL_VARS.free_app every 500ms
    const interval = setInterval(() => {
      setFreeAppStatus(prev => {
        if (prev !== GLOBAL_VARS.free_app) {
          return GLOBAL_VARS.free_app;
        }
        return prev;
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const handleDeepLink = async () => {
    return new Promise(async (resolve) => {
      const currentPath = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);

      // Handle hot-load deep links from any environment
      if (currentPath === '/hot-load') {
        const c = urlParams.get('c');
        if (c) {
          const dbHandler = DBHandler();
          const court_id = atob(c);
          const hot_load_check = isHotLoad();
          const hot_match_data_report = await dbHandler.hot_match_data_report();
          const was_hotmatch = hot_load_check ? hot_match_data_report.all_tables_exist : true;
          const old_court_id = hot_match_data_report.court_id;
          
          // console.log('hot_match_data_report: ', hot_match_data_report);
          // console.log('>>>>>> Deep Link Hot-Load Court:', court_id, '<<<<<');
          // console.log('was_hotmatch: ', was_hotmatch);
          // console.log('old_court_id: ', old_court_id);
          // console.log('hot_load_check: ', hot_load_check);
          // console.log('Check: ', old_court_id === court_id);
          
          if (was_hotmatch && old_court_id !== court_id) {
            console.warn('Hot-Load court mismatch, clearing hot-load');
            await clear_hot_load();
          } else if (was_hotmatch && old_court_id === court_id) {
            console.log('Checking Old HotMatch Data...')
            const appState = await dbHandler.load_app_state();
            const app_state_id = appState.id;
            const app_state_update = appState.update;

            if (app_state_id !== 10) {
              console.warn('App state ID mismatch');
              await clear_hot_load();
            } else {
              const now = Date.now();
              const updateTime = new Date(app_state_update).getTime();
              const diffMinutes = (now - updateTime) / (1000 * 60);
              if (diffMinutes > 10) {
                console.warn('match is too old, clearing hot-load');
                await clear_hot_load();
              }
            }
          } 


          GLOBAL_VARS.session_storage.set_court(court_id);
          GLOBAL_VARS.session_storage.set_hot_load("true");
          await beforeHotLoad(); // Not currently used -- keeping for now
          // window.history.replaceState({}, '', '/'); // Not sure what this was.
          resolve(true);
          return;
        } else {
          console.warn('Hot-load deep link missing court parameter');
        }
      }
      resolve(false);
    });
  };

  const init = async () => {
    if (appInitPromise) {
      return appInitPromise;
    }

    appInitPromise = (async () => {
      if (window.location.pathname === '/delete-db') {
        ConfirmModel({
          icon: 'warning',
          title: `Reset Local DB`,
          text: 'This will reset the local database and log you out. Are you sure you want to proceed?',
          confirmButtonText: 'Ok',
          allowOutsideClick: false,
          showCancelButton: true,
          cancelButtonText: 'Cancel',
        }).then((result) => {
          if (result.isConfirmed) {
            Logout()
          } else if (result.isDismissed && result.dismiss === 'cancel') {
            window.location = '/';
          }
        });
      }

      // Handle deep links first
      const wasDeepLink = await handleDeepLink();

      if (wasDeepLink) {
        console.log('Deep link processed, continuing with hot-load initialization...');
      }


      console.log('window.location.pathname: ', window.location.pathname);
      // Wait for proper routing
      do {
        await Sleep(100);
      } while (window.location.pathname !== '/score' && window.location.pathname !== '/hot-load' && window.location.pathname !== '/delete-db');
      // } while (window.location.pathname !== '/score' && window.location.pathname !== '/' && window.location.pathname !== '/hot-load');

      if (isHotLoad()) {
        window.history.replaceState({}, '', '/');



        const services = ServiceLauncherHotLoad();
        await services.init(async () => {
          init_variables();
        });
      } else {
        const services = ServiceLauncher();
        await services.init(async () => {
          init_variables();
        });
      }
    })();

    return appInitPromise;
  }

  const init_variables = async () => {
    // console.warn('>>>>>> init_variables <<<<<', GLOBAL_VARS.app_initiated);
    if (GLOBAL_VARS.app_initiated === false) {
      // console.log('>> init_variables <<',);
      await load_DB_vars();
      // console.log('completed loading');

      try {
        // const dbHandler = DBHandler();
        // const account = await dbHandler.load_account();
        await Wait_For_Auth(SME_AUTH);
        const userInfo = await SME_AUTH.get_user_info();
        if (userInfo !== false && typeof userInfo.username === 'string') {
          // console.log('Applying: ', userInfo.username);
          document.getElementById('accountUserName-eG').innerHTML = userInfo.username;
        } else {
          document.getElementById('accountUserName-eG').innerHTML = 'Guest';
        }
      } catch (error) {
        console.error('App - failed to load account for accountUserName-eG ', error);
      }
    }
    // setLoading(false);
  }


  useEffect(() => {
    init();

  }, []);


  useEffect(() => {
    NAV_CONTROL.appendCallback('appLoading', () => {

      setLoading(NAV_CONTROL.state.app_loading);
    });
    return () => {
      // >>> Runs on unLoad (Unload runs first if component is already mounted)
    };
  }, []); // >> [] -- Monitor


  return (
    <div className="App" data-orientation={isLandscape ? 'landscape' : 'portrait'}>
      {import.meta.env.PROD && <PWAUpdateHandler />}
      {free_app_status ?
        <div className='free-app-wrap'>
          <div className='free-app-text'>Subscription Required</div>
          <span>Please subscribe to continue using the app.</span>
          <button className='free-app-button' onClick={() => { SubscribeBtn(); }}>Subscribe</button>
          <button className='free-app-button' onClick={() => { LogoutBtn(); }}>Logout</button>
          <div style={{ marginTop: '20px', textAlign: 'center', color: '#fff' }}>
            <a
              href={import.meta.env.VITE_MEDIA_HOST?.replace(/^'+|'+$/g, '')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {mediaHost}
            </a>
          </div>
        </div>
        :
        <>
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <Head>
              {/* <Routes></Routes> */}
            </Head>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/score" element={<ScoringCore commonTriggers={commonTriggers} />} />
              <Route path="/hot-load" element={<LandingPage />} />
              {/* <Route path="/delete-db" element={<ScoringCore />} /> */}
              <Route path="*" element={<LandingPage />} />
            </Routes>
          </BrowserRouter>
          <MediaPlayer></MediaPlayer>
          <LoadingBlock show={loading} />
          <Overlay visibility={overlayVisibility}>{overlayElement}</Overlay>
        </>
      }

      <span id="accountUserName-eG" style={{ display: 'none' }}>Guest</span>
    </div>
  );
}

export default App;